import React from "react";
import { Navigate } from "react-router-dom";
import {
  LuLayoutDashboard,
  LuTag,
  LuMonitorSmartphone,
  LuNetwork,
  LuUsers,
  LuFileText,
  LuKey,
  LuShieldCheck,
  LuShieldAlert,
  LuUserCog,
  LuUserCheck,
  LuClipboardList,
  LuDatabase,
  LuContact,
  LuGlobe,
  LuWallet,
  LuCreditCard,
  LuReceipt,
  LuFileSpreadsheet,
  LuFileSignature,
  LuCalendarDays,
  LuHistory,
  LuActivity,
  LuSettings,
  LuMessageSquare,
  LuLogOut,
  LuLock,
  LuUser,
  LuStore,
  LuMonitorDot,
  LuShoppingCart,
  LuUtensils,
  LuShieldOff,
  LuUserX,
} from "react-icons/lu";

// Auth Imports
const LoginPage = React.lazy(() => import("views/auth/pages"));

// super user Imports — lazy-loaded for code splitting
const SuperUserDashboard = React.lazy(() => import("views/superuser/newDashboard/pages"));
const Brand = React.lazy(() => import("views/superuser/brand/pages"));
const BrandLocationsView = React.lazy(() => import("views/superuser/brand/pages/BrandLocationsView"));
const BrandSubBrandsView = React.lazy(() => import("views/superuser/brand/pages/BrandSubBrandsView"));
const Customers = React.lazy(() => import("views/superuser/customers/pages"));
const Settings = React.lazy(() => import("views/superuser/setting/pages"));
const Users = React.lazy(() => import("views/superuser/user/pages"));
const UsersModule = React.lazy(() => import("views/superuser/userModule/pages"));
const SignOut = React.lazy(() => import("views/auth/pages/signOut"));
const Stores = React.lazy(() => import("views/superuser/stores/page"));
const StoreSteps = React.lazy(() => import("views/superuser/stores/page/storeSteps"));
const Platform = React.lazy(() => import("views/superuser/platform/pages"));
const Payment = React.lazy(() => import("views/superuser/payment/pages"));
const InVoice = React.lazy(() => import("views/superuser/invoice/pages"));
const AdminUserManagement = React.lazy(() => import("views/superuser/adminUserManagement/pages"));
const ActivityLog = React.lazy(() => import("views/superuser/activitylog/pages"));
const OrderLogPage = React.lazy(() => import("views/superuser/logmonitoring/pages/orderLogPage"));
const AssignPlatform = React.lazy(() => import("views/superuser/platformAssign/pages"));
const Subscriptions = React.lazy(() => import("views/superuser/Subscriptions/pages"));
const BrandLocationManage = React.lazy(() => import("views/superuser/BrandLocatioManage/pages"));
const Notification = React.lazy(() => import("views/superuser/notification/pages"));
const AdminManage = React.lazy(() => import("views/superuser/adminmanage/pages"));
const GeneralSetting = React.lazy(() => import("views/superuser/generalSetting/pages"));
const DocumentManage = React.lazy(() => import("views/superuser/documentManage/pages"));
const PacakgeManage = React.lazy(() => import("views/superuser/packagemanage/pages"));
const AccessRoleMange = React.lazy(() => import("views/superuser/accessRole/pages"));
const CouponManage = React.lazy(() => import("views/superuser/couponmanage/pages"));
const CouponUsage = React.lazy(() => import("views/superuser/couponUsage/pages"));
const RoleModuleManage = React.lazy(() => import("views/superuser/rolemodule/pages"));
const Statement = React.lazy(() => import("views/superuser/statement/pages"));
// const Enums = React.lazy(() => import("views/superuser/enumConfiguration/pages"));
const Group = React.lazy(() => import("views/superuser/group/pages"));
const Inquiry = React.lazy(() => import("views/superuser/inquiry/pages"));
const ApplicationList = React.lazy(() => import("pages/Onboarding/Applications/ApplicationList"));
const ApplicationWorkspace = React.lazy(() => import("pages/Onboarding/Applications/ApplicationWorkspace"));
const OnboardingDashboard = React.lazy(() => import("pages/Onboarding/Dashboard/DashboardPage"));
const ReviewerAssignments = React.lazy(() => import("pages/Onboarding/Administration/ReviewerAssignments"));
const QuotationList = React.lazy(() => import("views/superuser/quotation/pages"));
const AddQuotation = React.lazy(() => import("views/superuser/quotation/pages/addQuotation"));

// Authentication & Security UI
const SecurityDashboard = React.lazy(() => import("views/superuser/securityDashboard/SecurityDashboard"));
const SessionsPage = React.lazy(() => import("views/superuser/sessions/SessionsPage"));
const AuditLogsPage = React.lazy(() => import("views/superuser/auditLogs/AuditLogsPage"));
const ThreatEventsPage = React.lazy(() => import("views/superuser/threatEvents/ThreatEventsPage"));
const IpRestrictionsPage = React.lazy(() => import("views/superuser/ipRestrictions/IpRestrictionsPage"));
const routes = [
  // routes shows on sidebar menu
  // maintain sequence to show sidebar menu
  {
    name: "Dashboard",
    layout: "/superuser",
    path: "dashboard",
    sidebar: true,
    module: "DASHBOARD",
    icon: <LuLayoutDashboard className="h-6 w-6" />,
    component: <SuperUserDashboard />,
  },
  {
    name: "Onboarding",
    layout: "/superuser",
    icon: <LuClipboardList className="h-5 w-6" />,
    children: [
      {
        name: "Dashboard",
        layout: "/superuser",
        path: "onboarding/dashboard",
        sidebar: true,
        module: "ONBOARDING_APPLICATION",
        icon: <LuLayoutDashboard className="h-5 w-5" />,
        component: <OnboardingDashboard />,
      },
      {
        name: "Onboarding Apps",
        layout: "/superuser",
        path: "onboarding/applications",
        sidebar: true,
        module: "ONBOARDING_APPLICATION",
        icon: <LuFileText className="h-5 w-5" />,
        component: <ApplicationList />,
      },
      {
        name: "Access / Assignment",
        layout: "/superuser",
        path: "onboarding/administration/reviewer-assignments",
        sidebar: true,
        module: "INTERNAL_STAFF",
        icon: <LuUserCheck className="h-5 w-5" />,
        component: <ReviewerAssignments />,
      },
    ]
  },
  {
    name: "Onboarding App Workspace",
    layout: "/superuser",
    path: "onboarding/applications/:id",
    sidebar: false,
    module: "ONBOARDING_APPLICATION",
    component: <ApplicationWorkspace />,
  },
  {
    name: "Brands",
    layout: "/superuser",
    path: "brand",
    sidebar: true,
    module: "BRAND",
    icon: <LuTag className="h-6 w-6" />,
    component: <Brand />,
  },
  {
    name: "Add Brand",
    layout: "/superuser",
    path: "brand/add",
    sidebar: false,
    component: <Brand />,
  },
  {
    name: "Edit Brand",
    layout: "/superuser",
    path: "brand/edit/:id",
    sidebar: false,
    component: <Brand />,
  },
  {
    name: "Brand Locations",
    layout: "/superuser",
    path: "brand/:id/locations",
    sidebar: false,
    component: <BrandLocationsView />,
  },
  {
    name: "Brand Sub-Brands",
    layout: "/superuser",
    path: "brand/:id/sub-brands",
    sidebar: false,
    component: <BrandSubBrandsView />,
  },
  {
    name: "Devices",
    layout: "/superuser",
    path: "device",
    sidebar: true,
    module: "DEVICE",
    icon: <LuMonitorSmartphone className="h-6 w-6" />,
    component: <Group />,
  },
  {
    name: "Add Group",
    layout: "/superuser",
    path: "device/add",
    sidebar: false,
    component: <Group />,
  },
  {
    name: "Edit Group",
    layout: "/superuser",
    path: "device/edit/:id",
    sidebar: false,
    component: <Group />,
  },
  {
    name: "Assign Platforms",
    layout: "/superuser",
    path: "assignplatform",
    sidebar: true,
    module: "ASSIGN_PLATFORM",
    icon: <LuNetwork className="h-6 w-6" />,
    component: <AssignPlatform />,
  },

  {
    name: "User Management",
    icon: <LuUsers className="h-5 w-6" />,
    layout: "/superuser",
    children: [
      {
        name: "Document",
        layout: "/superuser",
        path: "document-manage",
        sidebar: true,
        module: "DOCUMENT",
        icon: <LuFileText className="h-5 w-5" />,
        component: <DocumentManage />,
      },
      {
        name: "Document",
        layout: "/superuser",
        path: "document-manage/add",
        sidebar: false,
        component: <DocumentManage />,
      },
      {
        name: "Document",
        layout: "/superuser",
        path: "document-manage/edit/:id",
        sidebar: false,
        component: <DocumentManage />,
      },
      {
        name: "Role Module",
        layout: "/superuser",
        path: "role-module-manage",
        sidebar: true,
        module: "ROLE_MODULE",
        icon: <LuKey className="h-5 w-5" />,
        component: <RoleModuleManage />,
      },
      {
        name: "Role Module",
        layout: "/superuser",
        path: "role-module-manage/add",
        sidebar: false,
        component: <RoleModuleManage />,
      },
      {
        name: "Role Module",
        layout: "/superuser",
        path: "role-module-manage/edit/:id",
        sidebar: false,
        component: <RoleModuleManage />,
      },
      {
        name: "Access Role",
        layout: "/superuser",
        path: "accessRole",
        sidebar: true,
        module: "ACCESS_ROLE",
        icon: <LuShieldCheck className="h-5 w-5" />,
        component: <AccessRoleMange />,
      },
      {
        name: "Access Role",
        layout: "/superuser",
        path: "accessRole/add",
        sidebar: false,
        component: <AccessRoleMange />,
      },
      {
        name: "Access Role",
        layout: "/superuser",
        path: "accessRole/edit/:id",
        sidebar: false,
        component: <AccessRoleMange />,
      },
      {
        name: "Admin User",
        layout: "/superuser",
        path: "admin-user",
        sidebar: true,
        module: "ADMIN_USER",
        icon: <LuUserCog className="h-5 w-5" />,
        component: <AdminManage />,
      },
      {
        name: "Admin User",
        layout: "/superuser",
        path: "admin-user/add",
        sidebar: false,
        component: <AdminManage />,
      },
      {
        name: "Admin User",
        layout: "/superuser",
        path: "admin-user/edit/:id",
        sidebar: false,
        component: <AdminManage />,
      },
    ],
  },
  {
    name: "Master Table Data",
    icon: <LuDatabase className="h-5 w-6" />,
    layout: "/superuser",
    children: [
      {
        name: "Customers",
        layout: "/superuser",
        path: "customers",
        sidebar: true,
        module: "CUSTOMER",
        icon: <LuContact className="h-5 w-5" />,
        component: <Customers />,
      },
      {
        name: "Customers",
        layout: "/superuser",
        path: "customers/edit/:id",
        sidebar: false,
        component: <Customers />,
      },
      {
        name: "Platforms",
        layout: "/superuser",
        path: "platform",
        sidebar: true,
        module: "PLATFORM",
        icon: <LuGlobe className="h-5 w-5" />,
        component: <Platform />,
      },
      {
        name: "Platforms",
        layout: "/superuser",
        path: "platform/add",
        sidebar: false,
        component: <Platform />,
      },
      {
        name: "Platforms",
        layout: "/superuser",
        path: "platform/edit/:id",
        sidebar: false,
        component: <Platform />,
      },
      // {
      //   name: "Enums",
      //   layout: "/superuser",
      //   path: "enums",
      //   sidebar: true,
      //   icon: <AiOutlineOrderedList className="h-4 w-5" />,
      //   component: <Enums />,
      // },
      // {
      //   name: "Enums",
      //   layout: "/superuser",
      //   path: "enums/add",
      //   sidebar: false,
      //   component: <Enums />,
      // },
      // {
      //   name: "Enums",
      //   layout: "/superuser",
      //   path: "enums/edit",
      //   sidebar: false,
      //   component: <Enums />,
      // },
    ],
  },
  {
    name: "Billing",
    icon: <LuWallet className="h-5 w-6" />,
    layout: "/superuser",
    children: [
      {
        name: "Quotations",
        layout: "/superuser",
        path: "quotations",
        sidebar: true,
        icon: <LuFileSignature className="h-5 w-5" />,
        component: <QuotationList />,
      },
      {
        name: "Create Quotation",
        layout: "/superuser",
        path: "quotations/new",
        sidebar: false,
        component: <AddQuotation />,
      },
      {
        name: "Edit Quotation",
        layout: "/superuser",
        path: "quotations/edit/:id",
        sidebar: false,
        component: <AddQuotation />,
      },
      {
        name: "Quotation Details",
        layout: "/superuser",
        path: "quotations/:id",
        sidebar: false,
        component: <QuotationList />,
      },
      {
        name: "Payment",
        layout: "/superuser",
        path: "payment",
        sidebar: true,
        module: "PAYMENT",
        icon: <LuCreditCard className="h-5 w-5" />,
        component: <Payment />,
      },
      {
        name: "Payment",
        layout: "/superuser",
        path: "payment/add",
        sidebar: false,
        component: <Payment />,
      },
      {
        name: "Payment",
        layout: "/superuser",
        path: "payment/edit/:id",
        sidebar: false,
        component: <Payment />,
      },
      {
        name: "Invoice",
        layout: "/superuser",
        path: "invoice",
        sidebar: true,
        module: "INVOICE",
        icon: <LuReceipt className="h-5 w-5" />,
        component: <InVoice />,
      },
      {
        name: "Invoice",
        layout: "/superuser",
        path: "invoice/add",
        sidebar: false,
        component: <InVoice />,
      },
      {
        name: "Invoice",
        layout: "/superuser",
        path: "invoice/edit/:id",
        sidebar: false,
        icon: <LuReceipt className="h-5 w-5" />,
        component: <InVoice />,
      },
      {
        name: "Statement",
        layout: "/superuser",
        path: "statement",
        sidebar: true,
        module: "STATEMENT",
        icon: <LuFileSpreadsheet className="h-5 w-5" />,
        component: <Statement />,
      },
      {
        name: "Subscriptions",
        layout: "/superuser",
        path: "subscriptions",
        sidebar: true,
        module: "SUBSCRIPTION",
        icon: <LuCalendarDays className="h-5 w-5" />,
        component: <Subscriptions />,
      },
      {
        name: "Subscriptions",
        layout: "/superuser",
        path: "subscriptions/add",
        sidebar: false,
        component: <Subscriptions />,
      },
      {
        name: "Subscriptions",
        layout: "/superuser",
        path: "subscriptions/edit/:id",
        sidebar: false,
        component: <Subscriptions />,
      },
    ],
  },
  {
    name: "View Data History",
    icon: <LuHistory className="h-5 w-6" />,
    layout: "/superuser",
    children: [
      {
        name: "Activity Log",
        layout: "/superuser",
        path: "activitylog",
        sidebar: true,
        module: "ACTIVITY_LOG",
        icon: <LuActivity className="h-5 w-5" />,
        component: <ActivityLog />,
      },
    ],
  },
  {
    name: "General Settings",
    layout: "/superuser",
    path: "generalsettings",
    sidebar: true,
    module: "GENERAL_SETTING",
    icon: <LuSettings className="h-7 w-6" />,
    component: <GeneralSetting />,
  },
  {
    name: "Inquiry",
    layout: "/superuser",
    path: "inquiry",
    sidebar: true,
    module: "INQUIRY",
    icon: <LuMessageSquare className="h-6 w-6" />,
    component: <Inquiry />,
  },
  // {
  //   name: "Log Monitoring",
  //   icon: <LuMonitorDot className="h-5 w-6" />,
  //   layout: "/superuser",
  //   children: [
  //     {
  //       name: "Order Log",
  //       layout: "/superuser",
  //       path: "log-monitoring/order-log",
  //       sidebar: true,
  //       icon: <LuShoppingCart className="h-5 w-5" />,
  //       component: <OrderLogPage />,
  //     },
  //   ],
  // },
  {
    name: "Sign Out",
    layout: "/auth",
    path: "sign-out",
    sidebar: true,
    icon: <LuLogOut className="h-7 w-6" />,
    component: <SignOut />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/admin-user",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/access-role",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/role-module",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/admin-user/add",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/admin-user/edit/:id",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/access-role/add",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/access-role/edit/:id",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/role-module/add",
    sidebar: false,
    component: <AdminUserManagement />,
  },
  {
    name: "Admin User Management",
    layout: "/superuser",
    path: "admin-user-management/role-module/edit/:id",
    sidebar: false,
    component: <AdminUserManagement />,
  },

  {
    name: "Authentication & Security",
    icon: <LuShieldCheck className="h-5 w-6" />,
    layout: "/superuser",
    children: [
      {
        name: "Dashboard",
        layout: "/superuser",
        path: "security/dashboard",
        sidebar: true,
        icon: <LuActivity className="h-4 w-4" />,
        component: <SecurityDashboard />,
      },
      {
        name: "Sessions",
        layout: "/superuser",
        path: "security/sessions",
        sidebar: true,
        icon: <LuMonitorDot className="h-4 w-4" />,
        component: <SessionsPage />,
      },
      {
        name: "Audit Logs",
        layout: "/superuser",
        path: "security/audit-logs",
        sidebar: true,
        icon: <LuHistory className="h-4 w-4" />,
        component: <AuditLogsPage />,
      },
      {
        name: "Threat Events",
        layout: "/superuser",
        path: "security/threat-events",
        sidebar: true,
        icon: <LuShieldAlert className="h-4 w-4" />,
        component: <ThreatEventsPage />,
      },
      {
        name: "IP Restrictions & User Lockouts",
        layout: "/superuser",
        path: "security/ip-restrictions",
        sidebar: true,
        module: "IP_RESTRICTION",
        icon: <LuShieldOff className="h-4 w-4" />,
        component: <IpRestrictionsPage defaultTab="ip-restrictions" />,
      },
      {
        name: "User Lockouts",
        layout: "/superuser",
        path: "security/user-lockouts",
        sidebar: false,
        module: "IP_RESTRICTION",
        component: (
          <Navigate
            to="/superuser/security/ip-restrictions?tab=user-lockouts"
            replace
          />
        ),
      },
    ],
  },
  // routes not shows in sidebar menu
  {
    name: "Login",
    layout: "/",
    path: "login",
    icon: <LuLock className="h-7 w-6" />,
    component: <LoginPage />,
  },
  {
    name: "Stores",
    layout: "/superuser",
    path: "brand/stores/:brandId",
    icon: <LuStore className="h-7 w-6" />,
    component: <Stores />,
  },
  {
    name: "User",
    layout: "/superuser",
    path: "brand/stores/store/users/:brandId/:storeId",
    sidebar: false,
    icon: <LuUser className="h-5 w-6" />,
    component: <Users />,
  },
  {
    name: "Store",
    layout: "/superuser",
    path: "brand/stores/store/:brandId/:storeId",
    icon: <LuStore className="h-7 w-6" />,
    component: <StoreSteps />,
  },
  {
    name: "Setting",
    layout: "/superuser",
    path: "brand/stores/store/setting/:brandId/:storeId",
    sidebar: false,
    icon: <LuSettings className="h-7 w-6" />,
    component: <Settings />,
  },
];
export default routes;
